import { contextEngine } from './context-engine.js';
import { weatherService } from './weather-service.js';
import { notificationTimingService } from './notification-timing-service.js';
import { format, addMinutes, isSameDay, differenceInMinutes, addDays } from 'date-fns';

/**
 * Context Fusion Service for PaperSedge
 * 
 * Intelligently combines multiple context sources to provide
 * comprehensive insights and recommendations for family coordination.
 */
class ContextFusionService {
  constructor() {
    this.contextCache = new Map();
    this.fusionRules = new Map();
    this.lastFusionUpdate = null;
  }

  /**
   * Fuse multiple context sources into unified intelligence
   */
  async fuseContexts(userId, familyData) {
    try {
      // Gather all context sources
      const [
        baseContext,
        weatherContext,
        familyScheduleContext,
        transportationContext,
        communityContext
      ] = await Promise.all([
        contextEngine.buildContext(userId, familyData),
        this.getEnhancedWeatherContext(),
        this.analyzeFamilySchedules(familyData),
        this.analyzeTransportationNeeds(familyData),
        this.getCommunityContext()
      ]);

      // Perform multi-dimensional fusion
      const fusedContext = {
        ...baseContext,
        fusion: {
          weather: weatherContext,
          familySchedule: familyScheduleContext,
          transportation: transportationContext,
          community: communityContext,
          insights: await this.generateFusionInsights(
            baseContext,
            weatherContext,
            familyScheduleContext,
            transportationContext,
            communityContext
          ),
          opportunities: await this.identifyOpportunities(
            baseContext,
            familyScheduleContext,
            weatherContext
          ),
          conflicts: await this.detectConflicts(familyScheduleContext, transportationContext)
        }
      };

      // Cache the fused context
      this.contextCache.set(userId, {
        context: fusedContext,
        timestamp: new Date()
      });

      return fusedContext;
    } catch (error) {
      console.error('Error fusing contexts:', error);
      throw error;
    }
  }

  /**
   * Get enhanced weather context with multi-day planning
   */
  async getEnhancedWeatherContext() {
    try {
      const currentWeather = await weatherService.getCurrentWeather();
      const forecast = await weatherService.getWeatherForecast(null, 5);
      
      // Analyze weather patterns
      const patterns = this.analyzeWeatherPatterns(forecast);
      const opportunities = this.identifyWeatherOpportunities(forecast);
      
      return {
        current: currentWeather,
        forecast,
        patterns,
        opportunities,
        planning: {
          bestDaysThisWeek: this.findBestWeatherDays(forecast),
          outdoorWindows: this.findOutdoorWindows(forecast),
          weatherAlerts: this.checkWeatherAlerts(forecast)
        }
      };
    } catch (error) {
      console.error('Error getting enhanced weather context:', error);
      return null;
    }
  }

  /**
   * Analyze family schedules for coordination opportunities
   */
  async analyzeFamilySchedules(familyData) {
    const { family, familyCalendar, tasks, activities } = familyData;
    
    // Get all family members
    const familyMembers = [
      ...(family?.parents || []),
      ...(family?.kids || [])
    ];

    // Analyze individual schedules
    const memberSchedules = new Map();
    familyMembers.forEach(member => {
      const memberEvents = this.getMemberEvents(member.id, familyCalendar);
      const memberTasks = this.getMemberTasks(member.id, tasks);
      
      memberSchedules.set(member.id, {
        member,
        events: memberEvents,
        tasks: memberTasks,
        freeTime: this.calculateFreeTime(memberEvents),
        commitments: this.analyzeCommitments(memberEvents, memberTasks)
      });
    });

    // Find family-wide opportunities
    const familyOpportunities = this.findFamilyOpportunities(memberSchedules);
    const overlaps = this.findScheduleOverlaps(memberSchedules);
    const gaps = this.findCoordinationGaps(memberSchedules);

    return {
      memberSchedules: Array.from(memberSchedules.values()),
      familyOpportunities,
      overlaps,
      gaps,
      summary: {
        totalEvents: familyCalendar?.length || 0,
        totalTasks: tasks?.length || 0,
        busyScore: this.calculateFamilyBusyScore(memberSchedules),
        nextFreeSlot: this.findNextFamilyFreeSlot(memberSchedules)
      }
    };
  }

  /**
   * Analyze transportation needs and optimization opportunities
   */
  async analyzeTransportationNeeds(familyData) {
    const { familyCalendar, family } = familyData;
    
    // Extract events requiring transportation
    const transportEvents = this.extractTransportEvents(familyCalendar);
    
    // Group by time windows for potential carpooling
    const timeWindows = this.groupEventsByTimeWindow(transportEvents);
    
    // Find optimization opportunities
    const optimizations = this.findTransportOptimizations(timeWindows, family);
    
    // Calculate route efficiencies
    const routeAnalysis = this.analyzeRoutes(transportEvents);

    return {
      transportEvents,
      timeWindows,
      optimizations,
      routeAnalysis,
      recommendations: this.generateTransportRecommendations(optimizations)
    };
  }

  /**
   * Get community context (placeholder for future integration)
   */
  async getCommunityContext() {
    // This would integrate with local event APIs, school calendars, etc.
    return {
      localEvents: [],
      schoolEvents: [],
      communityActivities: [],
      seasonalOpportunities: this.getSeasonalOpportunities()
    };
  }

  /**
   * Generate insights from fused contexts
   */
  async generateFusionInsights(baseContext, weatherContext, familyScheduleContext, transportationContext, communityContext) {
    const insights = [];

    // Weather + Schedule fusion insights
    if (weatherContext && familyScheduleContext) {
      const weatherScheduleInsights = this.fuseWeatherAndSchedule(weatherContext, familyScheduleContext);
      insights.push(...weatherScheduleInsights);
    }

    // Transportation + Schedule fusion insights
    if (transportationContext && familyScheduleContext) {
      const transportScheduleInsights = this.fuseTransportAndSchedule(transportationContext, familyScheduleContext);
      insights.push(...transportScheduleInsights);
    }

    // Pattern + Schedule fusion insights
    if (baseContext.personal && familyScheduleContext) {
      const patternScheduleInsights = this.fusePatternAndSchedule(baseContext.personal, familyScheduleContext);
      insights.push(...patternScheduleInsights);
    }

    // Rank insights by impact and relevance
    return this.rankInsights(insights, baseContext);
  }

  /**
   * Identify coordination opportunities across contexts
   */
  async identifyOpportunities(baseContext, familyScheduleContext, weatherContext) {
    const opportunities = [];

    // Family activity opportunities
    if (familyScheduleContext.familyOpportunities.length > 0) {
      familyScheduleContext.familyOpportunities.forEach(opp => {
        // Enhance with weather data
        const weatherScore = weatherContext ? 
          this.scoreWeatherForActivity(weatherContext, opp.timeSlot) : 0.5;
        
        opportunities.push({
          type: 'family-activity',
          timeSlot: opp.timeSlot,
          participants: opp.participants,
          duration: opp.duration,
          weatherScore,
          confidence: opp.confidence * weatherScore,
          suggestions: this.generateActivitySuggestions(opp, weatherContext)
        });
      });
    }

    // Coordination windows
    const coordinationWindows = this.findCoordinationWindows(
      baseContext.personal,
      familyScheduleContext
    );
    opportunities.push(...coordinationWindows);

    return opportunities;
  }

  /**
   * Detect scheduling conflicts and transportation issues
   */
  async detectConflicts(familyScheduleContext, transportationContext) {
    const conflicts = [];

    // Time conflicts
    if (familyScheduleContext.overlaps.length > 0) {
      familyScheduleContext.overlaps.forEach(overlap => {
        conflicts.push({
          type: 'scheduling',
          severity: this.assessConflictSeverity(overlap),
          participants: overlap.participants,
          timeSlot: overlap.timeSlot,
          events: overlap.events,
          resolution: this.suggestConflictResolution(overlap)
        });
      });
    }

    // Transportation conflicts
    if (transportationContext) {
      const transportConflicts = this.detectTransportConflicts(
        transportationContext.transportEvents
      );
      conflicts.push(...transportConflicts);
    }

    return conflicts;
  }

  /**
   * Helper methods for weather analysis
   */
  analyzeWeatherPatterns(forecast) {
    if (!forecast || forecast.length === 0) return null;

    const patterns = {
      trend: this.detectWeatherTrend(forecast),
      bestDays: [],
      rainDays: [],
      extremeDays: []
    };

    forecast.forEach(day => {
      if (day.conditions === 'clear' && day.high >= 60 && day.high <= 85) {
        patterns.bestDays.push(day);
      }
      if (day.conditions === 'rain' || day.precipitation > 50) {
        patterns.rainDays.push(day);
      }
      if (day.high > 90 || day.low < 32) {
        patterns.extremeDays.push(day);
      }
    });

    return patterns;
  }

  identifyWeatherOpportunities(forecast) {
    const opportunities = [];
    
    forecast?.forEach((day, index) => {
      // Perfect weekend weather
      const dayOfWeek = new Date(day.date).getDay();
      if ((dayOfWeek === 0 || dayOfWeek === 6) && 
          day.conditions === 'clear' && 
          day.high >= 65 && day.high <= 80) {
        opportunities.push({
          type: 'perfect-weekend',
          date: day.date,
          conditions: day.conditions,
          temperature: { high: day.high, low: day.low },
          confidence: 0.9
        });
      }

      // Last good day before rain
      if (index < forecast.length - 1 && 
          day.conditions === 'clear' && 
          forecast[index + 1].conditions === 'rain') {
        opportunities.push({
          type: 'before-rain',
          date: day.date,
          message: 'Last good weather day before rain',
          confidence: 0.85
        });
      }
    });

    return opportunities;
  }

  /**
   * Helper methods for schedule analysis
   */
  getMemberEvents(memberId, calendar) {
    return calendar?.filter(event => 
      event.participants?.some(p => p.id === memberId)
    ) || [];
  }

  getMemberTasks(memberId, tasks) {
    return tasks?.filter(task => 
      task.assigned_to === memberId || task.created_by === memberId
    ) || [];
  }

  calculateFreeTime(events) {
    const freeSlots = [];
    const sortedEvents = events.sort((a, b) => 
      new Date(a.date + ' ' + (a.time || '00:00')) - 
      new Date(b.date + ' ' + (b.time || '00:00'))
    );

    // Find gaps between events
    for (let i = 0; i < sortedEvents.length - 1; i++) {
      const currentEnd = this.getEventEndTime(sortedEvents[i]);
      const nextStart = this.getEventStartTime(sortedEvents[i + 1]);
      
      const gap = differenceInMinutes(nextStart, currentEnd);
      if (gap > 60) { // At least 1 hour gap
        freeSlots.push({
          start: currentEnd,
          end: nextStart,
          duration: gap
        });
      }
    }

    return freeSlots;
  }

  findFamilyOpportunities(memberSchedules) {
    const opportunities = [];
    const members = Array.from(memberSchedules.values());
    
    // Find times when all family members are free
    const commonFreeSlots = this.findCommonFreeTime(members);
    
    commonFreeSlots.forEach(slot => {
      if (slot.duration >= 120) { // At least 2 hours
        opportunities.push({
          type: 'family-time',
          timeSlot: slot,
          participants: members.map(m => m.member),
          duration: slot.duration,
          confidence: this.calculateOpportunityConfidence(slot, members)
        });
      }
    });

    return opportunities;
  }

  /**
   * Transportation helper methods
   */
  extractTransportEvents(calendar) {
    return calendar?.filter(event => 
      event.location && event.location !== 'Home' && event.location !== 'Virtual'
    ) || [];
  }

  groupEventsByTimeWindow(events, windowMinutes = 30) {
    const windows = new Map();
    
    events.forEach(event => {
      const eventTime = new Date(event.date + ' ' + (event.time || '00:00'));
      const windowKey = Math.floor(eventTime.getTime() / (windowMinutes * 60 * 1000));
      
      if (!windows.has(windowKey)) {
        windows.set(windowKey, []);
      }
      windows.get(windowKey).push(event);
    });

    return Array.from(windows.values()).filter(group => group.length > 1);
  }

  findTransportOptimizations(timeWindows, family) {
    const optimizations = [];
    
    timeWindows.forEach(window => {
      // Check if multiple family members have events in same time window
      const participants = new Set();
      window.forEach(event => {
        event.participants?.forEach(p => participants.add(p.id));
      });

      if (participants.size > 1) {
        optimizations.push({
          type: 'carpool-opportunity',
          events: window,
          participants: Array.from(participants),
          timeSaved: this.estimateTimeSaved(window),
          confidence: 0.8
        });
      }
    });

    return optimizations;
  }

  /**
   * Fusion methods
   */
  fuseWeatherAndSchedule(weatherContext, familyScheduleContext) {
    const insights = [];
    
    // Check if good weather aligns with family free time
    weatherContext.planning.bestDaysThisWeek.forEach(day => {
      const daySchedule = this.getDaySchedule(day.date, familyScheduleContext);
      
      if (daySchedule.freeTime > 4) { // More than 4 hours free
        insights.push({
          type: 'weather-schedule-alignment',
          priority: 'high',
          date: day.date,
          title: 'Perfect Weather + Free Time',
          description: `${day.temperature}°F with clear skies aligns with family free time`,
          confidence: 0.9,
          actionable: true
        });
      }
    });

    return insights;
  }

  fuseTransportAndSchedule(transportationContext, familyScheduleContext) {
    const insights = [];
    
    transportationContext.optimizations.forEach(opt => {
      insights.push({
        type: 'transport-optimization',
        priority: 'medium',
        title: 'Carpool Opportunity',
        description: `Save ${opt.timeSaved} minutes by coordinating transportation`,
        events: opt.events,
        confidence: opt.confidence
      });
    });

    return insights;
  }

  fusePatternAndSchedule(personalContext, familyScheduleContext) {
    const insights = [];
    
    // Check if productivity peaks align with free time
    personalContext.productivityPeaks?.forEach(peak => {
      const peakSchedule = this.getTimeSchedule(peak, familyScheduleContext);
      
      if (peakSchedule.isFree) {
        insights.push({
          type: 'productivity-alignment',
          priority: 'medium',
          title: 'Productivity Peak Available',
          description: `Your ${peak} productivity peak aligns with free schedule`,
          confidence: 0.8
        });
      }
    });

    return insights;
  }

  /**
   * Utility methods
   */
  getEventStartTime(event) {
    return new Date(event.date + ' ' + (event.time || '00:00'));
  }

  getEventEndTime(event) {
    const start = this.getEventStartTime(event);
    const duration = event.duration || 60; // Default 1 hour
    return addMinutes(start, duration);
  }

  detectWeatherTrend(forecast) {
    if (!forecast || forecast.length < 3) return 'stable';
    
    const temps = forecast.map(f => f.high);
    const avgTemp = temps.reduce((a, b) => a + b, 0) / temps.length;
    const firstHalf = temps.slice(0, Math.floor(temps.length / 2));
    const secondHalf = temps.slice(Math.floor(temps.length / 2));
    
    const firstAvg = firstHalf.reduce((a, b) => a + b, 0) / firstHalf.length;
    const secondAvg = secondHalf.reduce((a, b) => a + b, 0) / secondHalf.length;
    
    if (secondAvg > firstAvg + 5) return 'warming';
    if (secondAvg < firstAvg - 5) return 'cooling';
    return 'stable';
  }

  findBestWeatherDays(forecast) {
    return forecast?.filter(day => 
      day.conditions === 'clear' && 
      day.high >= 65 && 
      day.high <= 85 &&
      day.precipitation < 20
    ) || [];
  }

  findOutdoorWindows(forecast) {
    const windows = [];
    
    forecast?.forEach(day => {
      day.hours?.forEach(hour => {
        if (hour.conditions !== 'rain' && 
            hour.temp >= 60 && 
            hour.temp <= 85) {
          windows.push({
            date: day.date,
            hour: hour.time,
            temp: hour.temp,
            conditions: hour.conditions
          });
        }
      });
    });

    return windows;
  }

  checkWeatherAlerts(forecast) {
    const alerts = [];
    
    forecast?.forEach(day => {
      if (day.high > 90) {
        alerts.push({
          type: 'heat',
          date: day.date,
          severity: 'medium',
          message: `High temperature alert: ${day.high}°F`
        });
      }
      if (day.conditions === 'thunderstorm') {
        alerts.push({
          type: 'storm',
          date: day.date,
          severity: 'high',
          message: 'Thunderstorm expected'
        });
      }
    });

    return alerts;
  }

  findCommonFreeTime(members) {
    // Complex algorithm to find overlapping free time
    // Simplified version for now
    return [{
      start: new Date(),
      end: addMinutes(new Date(), 120),
      duration: 120
    }];
  }

  calculateOpportunityConfidence(slot, members) {
    // Base confidence on number of members and slot duration
    const memberFactor = Math.min(members.length / 4, 1);
    const durationFactor = Math.min(slot.duration / 180, 1); // 3 hours optimal
    return memberFactor * 0.5 + durationFactor * 0.5;
  }

  rankInsights(insights, context) {
    // Sort by priority and relevance
    return insights.sort((a, b) => {
      const priorityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
      const aPriority = priorityOrder[a.priority] || 0;
      const bPriority = priorityOrder[b.priority] || 0;
      
      if (aPriority !== bPriority) return bPriority - aPriority;
      return (b.confidence || 0.5) - (a.confidence || 0.5);
    });
  }

  generateActivitySuggestions(opportunity, weatherContext) {
    const suggestions = [];
    const isGoodWeather = weatherContext?.current?.outdoorSuitability === 'excellent' || 
                         weatherContext?.current?.outdoorSuitability === 'good';

    if (isGoodWeather && opportunity.duration >= 120) {
      suggestions.push('Family hike or nature walk');
      suggestions.push('Picnic in the park');
      suggestions.push('Outdoor sports or games');
    } else {
      suggestions.push('Board game session');
      suggestions.push('Movie night');
      suggestions.push('Cooking together');
    }

    return suggestions;
  }

  findCoordinationWindows(personalContext, familyScheduleContext) {
    const windows = [];
    
    personalContext.preferredCommunicationTimes?.forEach(time => {
      const [period, hour] = time.split('-');
      windows.push({
        type: 'coordination-window',
        time: { period, hour: parseInt(hour) || null },
        confidence: 0.8,
        reason: 'Preferred communication time'
      });
    });

    return windows;
  }

  assessConflictSeverity(overlap) {
    if (overlap.events.length > 2) return 'high';
    if (overlap.events.some(e => e.priority === 'high')) return 'high';
    if (overlap.duration > 30) return 'medium';
    return 'low';
  }

  suggestConflictResolution(overlap) {
    const suggestions = [];
    
    // Time shifting
    suggestions.push({
      type: 'time-shift',
      description: 'Move one event 30 minutes earlier or later'
    });

    // Delegation
    if (overlap.participants.length > 1) {
      suggestions.push({
        type: 'delegate',
        description: 'Split attendance between family members'
      });
    }

    return suggestions;
  }

  detectTransportConflicts(transportEvents) {
    const conflicts = [];
    
    // Find events that require same driver at overlapping times
    for (let i = 0; i < transportEvents.length; i++) {
      for (let j = i + 1; j < transportEvents.length; j++) {
        if (this.eventsOverlap(transportEvents[i], transportEvents[j])) {
          conflicts.push({
            type: 'transportation',
            severity: 'high',
            events: [transportEvents[i], transportEvents[j]],
            reason: 'Simultaneous transportation needs'
          });
        }
      }
    }

    return conflicts;
  }

  eventsOverlap(event1, event2) {
    const start1 = this.getEventStartTime(event1);
    const end1 = this.getEventEndTime(event1);
    const start2 = this.getEventStartTime(event2);
    const end2 = this.getEventEndTime(event2);
    
    return (start1 < end2 && end1 > start2);
  }

  scoreWeatherForActivity(weatherContext, timeSlot) {
    if (!weatherContext || !weatherContext.current) return 0.5;
    
    const { temperature, conditions } = weatherContext.current;
    let score = 0.5;
    
    if (conditions === 'clear' && temperature >= 65 && temperature <= 80) {
      score = 0.9;
    } else if (conditions === 'clouds' && temperature >= 60 && temperature <= 85) {
      score = 0.7;
    } else if (conditions === 'rain') {
      score = 0.2;
    }
    
    return score;
  }

  estimateTimeSaved(events) {
    // Simple estimate: 15 minutes per consolidated trip
    return Math.min(events.length * 15, 60);
  }

  getDaySchedule(date, familyScheduleContext) {
    const dayEvents = familyScheduleContext.memberSchedules
      .flatMap(m => m.events)
      .filter(e => isSameDay(new Date(e.date), new Date(date)));
    
    const totalHours = 24;
    const busyHours = dayEvents.reduce((sum, e) => sum + (e.duration || 60) / 60, 0);
    
    return {
      events: dayEvents,
      freeTime: totalHours - busyHours,
      busyScore: busyHours / totalHours
    };
  }

  getTimeSchedule(period, familyScheduleContext) {
    // Check if the time period is free across family schedule
    return {
      isFree: true, // Simplified
      conflicts: []
    };
  }

  generateTransportRecommendations(optimizations) {
    return optimizations.map(opt => ({
      type: 'transport',
      priority: opt.timeSaved > 30 ? 'high' : 'medium',
      title: `Carpool opportunity saves ${opt.timeSaved} minutes`,
      events: opt.events,
      action: 'coordinate-transport'
    }));
  }

  getSeasonalOpportunities() {
    const month = new Date().getMonth();
    const season = month >= 2 && month <= 4 ? 'spring' :
                  month >= 5 && month <= 7 ? 'summer' :
                  month >= 8 && month <= 10 ? 'fall' : 'winter';
    
    const opportunities = {
      spring: ['Nature walks', 'Garden planning', 'Spring cleaning'],
      summer: ['Beach trips', 'Camping', 'Outdoor concerts'],
      fall: ['Apple picking', 'Hiking', 'Fall festivals'],
      winter: ['Ice skating', 'Holiday activities', 'Indoor crafts']
    };
    
    return opportunities[season] || [];
  }

  calculateFamilyBusyScore(memberSchedules) {
    const scores = Array.from(memberSchedules.values()).map(schedule => {
      const totalEvents = schedule.events.length;
      const totalTasks = schedule.tasks.length;
      return (totalEvents + totalTasks) / 10; // Normalize
    });
    
    return Math.min(scores.reduce((a, b) => a + b, 0) / scores.length, 1);
  }

  findNextFamilyFreeSlot(memberSchedules) {
    // Simplified: return next 2-hour window
    return {
      start: addMinutes(new Date(), 120),
      end: addMinutes(new Date(), 240),
      duration: 120
    };
  }

  analyzeCommitments(events, tasks) {
    const totalCommitments = events.length + tasks.length;
    const highPriorityCommitments = [
      ...events.filter(e => e.priority === 'high'),
      ...tasks.filter(t => t.priority === 'high')
    ].length;
    
    return {
      total: totalCommitments,
      highPriority: highPriorityCommitments,
      distribution: this.analyzeCommitmentDistribution(events, tasks)
    };
  }

  analyzeCommitmentDistribution(events, tasks) {
    return {
      morning: events.filter(e => parseInt(e.time?.split(':')[0] || 0) < 12).length,
      afternoon: events.filter(e => {
        const hour = parseInt(e.time?.split(':')[0] || 0);
        return hour >= 12 && hour < 17;
      }).length,
      evening: events.filter(e => parseInt(e.time?.split(':')[0] || 0) >= 17).length
    };
  }

  findScheduleOverlaps(memberSchedules) {
    // Simplified implementation
    return [];
  }

  findCoordinationGaps(memberSchedules) {
    // Simplified implementation
    return [];
  }

  analyzeRoutes(transportEvents) {
    // Simplified route analysis implementation
    return {
      totalRoutes: transportEvents.length,
      averageDistance: 15, // miles
      averageTime: 25, // minutes
      efficiency: 0.8,
      suggestions: []
    };
  }
}

// Export singleton instance
export const contextFusionService = new ContextFusionService();
export default contextFusionService;